<script lang="ts">
  import { api } from '$shared/api';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import Avatar from '$shared/Avatar.svelte';

  /**
   * Unified saved-tracks page. All saved tracks are now one entity (one
   * `saved_track` row keyed by track_id), surfaced via GET /users/current/saved
   * as a single flat list.
   */
  interface SavedItem {
    id: number;
    title: string | null;
    artist: string | null;
    style: string | null;
    submitter: string | null;
    avatar_url: string | null;
    duel_id: number | null;
    duel_name: string | null;
    type: string;
    youtube_id: string | null;
    soundcloud_url: string | null;
    tiktok_url: string | null;
    yt_view_count: number | null;
    lb_listen_count: number | null;
    favorited_at: string;
  }

  let items = $state<SavedItem[]>([]);
  let loading = $state(true);

  $effect(() => {
    loadSaved();
  });

  async function loadSaved() {
    try {
      const data = await api.get<{ items: SavedItem[] }>('/users/current/saved');
      items = data.items;
    } catch {
      addToast('Could not load saved tracks', 'error');
    }
    loading = false;
  }

  async function removeFavorite(item: SavedItem) {
    try {
      await api.delete(`/tracks/${item.id}/favorite`);
      items = items.filter((x) => x.id !== item.id);
      addToast('Removed from saved', 'join');
    } catch {
      addToast('Could not remove', 'error');
    }
  }

  function detailHref(it: SavedItem): string {
    return `#/tracks/${it.id}`;
  }

  function formatCount(n: number | null): string {
    if (n == null) return '';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return String(n);
  }
</script>

<div class="narrow">
  <h1>Saved Tracks</h1>

  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if items.length === 0}
    <div class="card card--no-hover empty-state">
      <p>No saved tracks yet. Tap the &hearts; button while listening to save tracks.</p>
    </div>
  {:else}
    <div class="fav-list">
      {#each items as it (it.id)}
        <div class="fav-card card card--no-hover">
          <div class="fav-card__main">
            <div class="fav-card__info">
              <a href={detailHref(it)} class="fav-card__title">
                {it.title || 'Untitled'}
              </a>
              <div class="fav-card__meta">
                {#if it.submitter}
                  <span class="fav-card__user">
                    <Avatar username={it.submitter} avatarUrl={it.avatar_url} size="xs" />
                    {it.submitter}
                  </span>
                {/if}
                {#if it.duel_name}
                  <a href="#/duels/{it.duel_id}/result" class="fav-card__duel">{it.duel_name}</a>
                {/if}
                {#if it.artist}<span class="fav-card__artist">{it.artist}</span>{/if}
                {#if it.style}<span class="fav-card__style">{it.style}</span>{/if}
                {#if it.yt_view_count}<span class="fav-card__metric">{formatCount(it.yt_view_count)} views</span>{/if}
              </div>
            </div>
            <button class="btn-remove" onclick={() => removeFavorite(it)} title="Remove from saved">&hearts;</button>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 0.75rem;
  }
  .empty-state {
    text-align: center;
    padding: 2rem;
    color: var(--text-secondary);
  }
  .fav-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .fav-card {
    padding: 0.75rem 1rem;
  }
  .fav-card__main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .fav-card__info {
    flex: 1;
    min-width: 0;
  }
  .fav-card__title {
    display: block;
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text);
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fav-card__title:hover {
    color: var(--cyan);
  }
  .fav-card__meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: var(--text-muted);
    margin-top: 0.25rem;
    flex-wrap: wrap;
  }
  .fav-card__user {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .fav-card__duel,
  .fav-card__artist {
    color: var(--text-secondary);
    text-decoration: none;
  }
  .fav-card__duel:hover {
    color: var(--cyan);
  }
  .fav-card__style,
  .fav-card__metric {
    color: var(--text-muted);
  }
  .btn-remove {
    background: none;
    border: none;
    color: var(--pink);
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0.25rem;
    opacity: 0.7;
    transition: opacity 0.2s;
    flex-shrink: 0;
  }
  .btn-remove:hover {
    opacity: 1;
  }
</style>
